import { useCallback, useEffect, useState, type FocusEvent, type KeyboardEvent } from 'react';

/**
 * Fase 2 (W1, D28, spec F5): menu lateral recolhível no desktop. Abre aberto; a escolha fica por usuário no aparelho.
 * Recolhido, vira trilho de ícones e "espia" (abre por cima do conteúdo) com o foco do teclado dentro dele, na hora
 * (WCAG 1.4.13), ou com o mouse. Fase 2 (W31, item 3 do Gustavo): o mouse só abre a espiada depois de 10 s PARADO sobre o
 * trilho (mexer recomeça a contagem); ao sair, fecha na hora. O clique num ícone do trilho não abre a espiada (só o foco
 * visível do teclado, `:focus-visible`).
 */
export const chaveMenuRecolhido = (usuarioId: string) => `malgoos.menu.recolhido.${usuarioId}`;
export const INTENCAO_MS = 10_000;

type Agendar = (f: () => void, ms: number) => unknown;
type Cancelar = (id: never) => void;

/**
 * Relógio da espiada pelo mouse (puro, testado com relógio falso em `tests/w31-navegacao.test.ts`): `entrar` começa a contar,
 * `mover` recomeça enquanto a espiada não abriu (o mouse precisa ficar parado), `sair` cancela. `aoEspiar` dispara depois
 * de `INTENCAO_MS` sem movimento.
 */
export function criarIntencaoMouse(aoEspiar: () => void, agendar: Agendar = setTimeout, cancelar: Cancelar = clearTimeout as Cancelar) {
  let id: unknown = null;
  const limpar = () => {
    if (id !== null) cancelar(id as never);
    id = null;
  };
  const contar = () => {
    limpar();
    id = agendar(() => {
      id = null;
      aoEspiar();
    }, INTENCAO_MS);
  };
  return {
    entrar: contar,
    mover: () => {
      if (id !== null) contar();
    },
    sair: limpar,
    limpar,
    get contando() {
      return id !== null;
    },
  };
}

/** Foco que veio do teclado (o clique de mouse num botão ou link não tem `:focus-visible`). */
export function focoDoTeclado(el: EventTarget | null): boolean {
  const alvo = el as { matches?: (seletor: string) => boolean } | null;
  if (!alvo || typeof alvo.matches !== 'function') return false;
  try {
    return alvo.matches(':focus-visible');
  } catch {
    // Navegador sem `:focus-visible`: abre como antes (o foco do teclado nunca fica sem a espiada).
    return true;
  }
}

export function lerMenuRecolhido(usuarioId: string): boolean {
  try {
    return localStorage.getItem(chaveMenuRecolhido(usuarioId)) === '1';
  } catch {
    return false;
  }
}

export function useMenuRecolhido(usuarioId: string) {
  const [recolhido, setRecolhido] = useState(() => lerMenuRecolhido(usuarioId));
  useEffect(() => setRecolhido(lerMenuRecolhido(usuarioId)), [usuarioId]);
  const alternar = useCallback(() => {
    setRecolhido((atual) => {
      const novo = !atual;
      try {
        if (novo) localStorage.setItem(chaveMenuRecolhido(usuarioId), '1');
        else localStorage.removeItem(chaveMenuRecolhido(usuarioId));
      } catch {
        /* sem storage: vale até recarregar */
      }
      return novo;
    });
  }, [usuarioId]);
  return [recolhido, alternar] as const;
}

/** Espiar o trilho: mouse parado por `INTENCAO_MS`, foco do teclado na hora; Esc fecha até o próximo foco ou mouse. */
export function useEspiar(ativo: boolean) {
  const [mouse, setMouse] = useState(false);
  const [foco, setFoco] = useState(false);
  // Um relógio por montagem (estado preguiçoso: nada de ler ref durante o render).
  const [relogio] = useState(() => criarIntencaoMouse(() => setMouse(true)));
  useEffect(() => {
    if (!ativo) {
      relogio.limpar();
      setMouse(false);
      setFoco(false);
    }
    return () => relogio.limpar();
  }, [ativo, relogio]);
  const props = {
    onMouseEnter: () => {
      if (!ativo) return;
      relogio.entrar();
    },
    onMouseMove: () => {
      if (ativo) relogio.mover();
    },
    onMouseLeave: () => {
      relogio.sair();
      setMouse(false);
    },
    onFocus: (e: FocusEvent<HTMLElement>) => ativo && focoDoTeclado(e.target) && setFoco(true),
    onBlur: (e: FocusEvent<HTMLElement>) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFoco(false);
    },
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
      if (e.key !== 'Escape' || !(mouse || foco)) return;
      setMouse(false);
      setFoco(false);
    },
  };
  return { espiando: ativo && (mouse || foco), props };
}
